import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Trust badges for property cards.
 *
 * Reads /api/listings/{listing_id}/badges and renders Pulse Verified +
 * Google Verified pills only when the listing actually qualifies.
 *
 * Safe to drop into PropertyCard / PropertyDetail. Fails silently if
 * the listing has no badges so cards stay clean.
 */
const ListingBadges = ({ listingId, compact = false, dataTestId = 'listing-badges' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!listingId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/listings/${listingId}/badges`);
        if (!cancelled) setData(r.data?.badges || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [listingId]);

  if (!data || (!data.google_verified && !data.pulse_verified)) return null;

  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? '' : 'mt-2'}`} data-testid={dataTestId}>
      {data.google_verified && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full"
          title="Agent verified against Google business records"
          data-testid={`${dataTestId}-google`}
        >
          <CheckCircle2 className="h-3 w-3" />
          Google-Verified
        </span>
      )}
      {data.pulse_verified && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full"
          title="Pulse-checked for accuracy in the last 24 hours"
          data-testid={`${dataTestId}-pulse`}
        >
          <ShieldCheck className="h-3 w-3" />
          Pulse-Verified
        </span>
      )}
    </div>
  );
};

export default ListingBadges;
